import React, {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  supplierOpeningBalancesApi,
} from "../api/supplierOpeningBalancesApi";

import {
  suppliersApi,
} from "../api/suppliersApi";

import type {
  SupplierRecord,
} from "../types/suppliers.types";

import type {
  SupplierOpeningBalanceRecord,
  SupplierOpeningBalanceSummary,
  SupplierOpeningBalanceType,
} from "../types/supplier-opening-balances.types";

import "../screenstyles/SupplierOpeningBalances.css";


type FormState = {
  balanceType:
    SupplierOpeningBalanceType;

  amount: string;

  currencyCode: string;

  baseCurrencyCode: string;

  exchangeRate: string;

  balanceDate: string;

  referenceNumber: string;

  notes: string;
};


const today =
  new Date()
    .toISOString()
    .slice(0, 10);


const initialForm: FormState = {
  balanceType:
    "payable",

  amount:
    "",

  currencyCode:
    "IQD",

  baseCurrencyCode:
    "IQD",

  exchangeRate:
    "1",

  balanceDate:
    today,

  referenceNumber:
    "",

  notes:
    "",
};


function formatAmount(
  value: number,
): string {
  return new Intl.NumberFormat(
    "en-US",
    {
      maximumFractionDigits: 3,
    },
  ).format(value);
}


function SupplierOpeningBalances() {
  const [
    suppliers,
    setSuppliers,
  ] = useState<
    SupplierRecord[]
  >([]);

  const [
    selectedSupplierId,
    setSelectedSupplierId,
  ] = useState("");

  const [
    balances,
    setBalances,
  ] = useState<
    SupplierOpeningBalanceRecord[]
  >([]);

  const [
    summary,
    setSummary,
  ] = useState<
    SupplierOpeningBalanceSummary | null
  >(null);

  const [
    form,
    setForm,
  ] = useState<FormState>(
    initialForm,
  );

  const [
    editingBalanceId,
    setEditingBalanceId,
  ] = useState<
    string | null
  >(null);

  const [
    loadingSuppliers,
    setLoadingSuppliers,
  ] = useState(true);

  const [
    loadingBalances,
    setLoadingBalances,
  ] = useState(false);

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const [
    successMessage,
    setSuccessMessage,
  ] = useState("");


  const selectedSupplier =
    useMemo(
      () =>
        suppliers.find(
          supplier =>
            supplier.id ===
            selectedSupplierId,
        ) ?? null,
      [
        suppliers,
        selectedSupplierId,
      ],
    );


  const loadSuppliers =
    useCallback(
      async () => {
        setLoadingSuppliers(
          true,
        );

        setError("");

        try {
          let cursor:
            string | undefined;

          const allSuppliers:
            SupplierRecord[] = [];

          do {
            const result =
              await suppliersApi.list({
                isActive: true,
                sort:
                  "name_asc",
                limit:
                  100,
                cursor,
              });

            allSuppliers.push(
              ...result.items,
            );

            cursor =
              result.pagination
                .hasMore
                ? result.pagination
                    .nextCursor ??
                  undefined
                : undefined;
          } while (cursor);

          setSuppliers(
            allSuppliers,
          );
        } catch (err) {
          console.error(
            err,
          );

          setError(
            "تعذر تحميل الموردين.",
          );
        } finally {
          setLoadingSuppliers(
            false,
          );
        }
      },
      [],
    );


  const loadBalances =
    useCallback(
      async (
        supplierId: string,
      ) => {
        if (!supplierId) {
          setBalances([]);
          setSummary(null);
          return;
        }

        setLoadingBalances(
          true,
        );

        setError("");

        try {
          const [
            balancesResult,
            summaryResult,
          ] =
            await Promise.all([
              supplierOpeningBalancesApi.list(
                supplierId,
                {
                  limit: 100,
                  sort:
                    "balanceDate_desc",
                },
              ),

              supplierOpeningBalancesApi.getSummary(
                supplierId,
              ),
            ]);

          setBalances(
            balancesResult.items,
          );

          setSummary(
            summaryResult,
          );
        } catch (err) {
          console.error(
            err,
          );

          setError(
            "تعذر تحميل الأرصدة الافتتاحية.",
          );
        } finally {
          setLoadingBalances(
            false,
          );
        }
      },
      [],
    );


  useEffect(
    () => {
      void loadSuppliers();
    },
    [
      loadSuppliers,
    ],
  );


  useEffect(
    () => {
      if (
        selectedSupplierId
      ) {
        void loadBalances(
          selectedSupplierId,
        );
      } else {
        setBalances([]);
        setSummary(null);
      }
    },
    [
      selectedSupplierId,
      loadBalances,
    ],
  );


  useEffect(
    () => {
      if (
        selectedSupplier &&
        !editingBalanceId
      ) {
        setForm(
          previous => ({
            ...previous,

            currencyCode:
              selectedSupplier
                .currencyCode ||
              "IQD",
          }),
        );
      }
    },
    [
      selectedSupplier,
      editingBalanceId,
    ],
  );


  function resetForm() {
    setEditingBalanceId(
      null,
    );

    setForm({
      ...initialForm,

      currencyCode:
        selectedSupplier
          ?.currencyCode ||
        "IQD",
    });
  }


  function handleSupplierChange(
    supplierId: string,
  ) {
    setSelectedSupplierId(
      supplierId,
    );

    setEditingBalanceId(
      null,
    );

    setSuccessMessage(
      "",
    );

    setError(
      "",
    );
  }


  function handleEdit(
    item:
      SupplierOpeningBalanceRecord,
  ) {
    setEditingBalanceId(
      item.balanceId,
    );

    setForm({
      balanceType:
        item.balanceType,

      amount:
        String(
          item.amount,
        ),

      currencyCode:
        item.currencyCode,

      baseCurrencyCode:
        item.baseCurrencyCode,

      exchangeRate:
        String(
          item.exchangeRate,
        ),

      balanceDate:
        item.balanceDate,

      referenceNumber:
        item.referenceNumber ??
        "",

      notes:
        item.notes ??
        "",
    });

    window.scrollTo({
      top: 0,
      behavior:
        "smooth",
    });
  }


  async function handleSubmit(
    event:
      FormEvent<HTMLFormElement>,
  ) {
    event.preventDefault();

    if (
      !selectedSupplierId
    ) {
      setError(
        "اختر المورد أولاً.",
      );

      return;
    }

    const amount =
      Number(
        form.amount,
      );

    const exchangeRate =
      Number(
        form.exchangeRate,
      );

    if (
      !Number.isFinite(
        amount,
      ) ||
      amount <= 0
    ) {
      setError(
        "أدخل مبلغًا صحيحًا أكبر من صفر.",
      );

      return;
    }

    if (
      !Number.isFinite(
        exchangeRate,
      ) ||
      exchangeRate <= 0
    ) {
      setError(
        "أدخل سعر صرف صحيحًا.",
      );

      return;
    }

    if (
      form.currencyCode
        .trim()
        .toUpperCase() ===
        form.baseCurrencyCode
          .trim()
          .toUpperCase() &&
      exchangeRate !== 1
    ) {
      setError(
        "عندما تكون العملتان متطابقتين يجب أن يكون سعر الصرف 1.",
      );

      return;
    }

    setSaving(true);

    setError("");

    setSuccessMessage(
      "",
    );

    const payload = {
      balanceType:
        form.balanceType,

      amount,

      currencyCode:
        form.currencyCode
          .trim()
          .toUpperCase(),

      baseCurrencyCode:
        form.baseCurrencyCode
          .trim()
          .toUpperCase(),

      exchangeRate,

      balanceDate:
        form.balanceDate,

      referenceNumber:
        form.referenceNumber
          .trim() ||
        null,

      notes:
        form.notes.trim() ||
        null,
    };

    try {
      if (
        editingBalanceId
      ) {
        await supplierOpeningBalancesApi.update(
          selectedSupplierId,
          editingBalanceId,
          payload,
        );

        setSuccessMessage(
          "تم تعديل الرصيد الافتتاحي بنجاح.",
        );
      } else {
        await supplierOpeningBalancesApi.create(
          selectedSupplierId,
          payload,
        );

        setSuccessMessage(
          "تمت إضافة الرصيد الافتتاحي بنجاح.",
        );
      }

      resetForm();

      await loadBalances(
        selectedSupplierId,
      );
    } catch (err) {
      console.error(
        err,
      );

      setError(
        "تعذر حفظ الرصيد الافتتاحي. تحقق من البيانات أو من وجود رصيد سابق بنفس العملة.",
      );
    } finally {
      setSaving(false);
    }
  }


  async function handleCancelBalance(
    item:
      SupplierOpeningBalanceRecord,
  ) {
    const reason =
      window.prompt(
        "اكتب سبب إلغاء الرصيد الافتتاحي:",
      );

    if (
      reason === null
    ) {
      return;
    }

    if (
      !reason.trim()
    ) {
      setError(
        "سبب الإلغاء مطلوب.",
      );

      return;
    }

    const confirmed =
      window.confirm(
        "هل أنت متأكد من إلغاء هذا الرصيد؟ لن يتم حذفه من السجل المحاسبي.",
      );

    if (
      !confirmed
    ) {
      return;
    }

    setError("");

    setSuccessMessage(
      "",
    );

    try {
      await supplierOpeningBalancesApi.cancel(
        item.supplierId,
        item.balanceId,
        {
          reason:
            reason.trim(),
        },
      );

      if (
        editingBalanceId ===
        item.balanceId
      ) {
        resetForm();
      }

      setSuccessMessage(
        "تم إلغاء الرصيد الافتتاحي.",
      );

      await loadBalances(
        item.supplierId,
      );
    } catch (err) {
      console.error(
        err,
      );

      setError(
        "تعذر إلغاء الرصيد الافتتاحي.",
      );
    }
  }


  return (
    <div
      className="supplier-opening-page"
      dir="rtl"
    >
      <section className="supplier-opening-hero">
        <div>
          <span className="supplier-opening-eyebrow">
            الحسابات الدائنة
          </span>

          <h1>
            الأرصدة الافتتاحية للموردين
          </h1>

          <p>
            تسجيل الديون والأرصدة السابقة
            للموردين قبل بدء العمل على
            النظام المحاسبي.
          </p>
        </div>
      </section>


      {error && (
        <div className="supplier-opening-alert error">
          {error}
        </div>
      )}


      {successMessage && (
        <div className="supplier-opening-alert success">
          {successMessage}
        </div>
      )}


      <section className="supplier-opening-card">
        <div className="supplier-opening-section-title">
          <div>
            <h2>
              اختيار المورد
            </h2>

            <p>
              اختر المورد لعرض أو تسجيل
              رصيده السابق.
            </p>
          </div>
        </div>

        <select
          className="supplier-opening-select supplier-selector"
          value={
            selectedSupplierId
          }
          disabled={
            loadingSuppliers
          }
          onChange={
            event =>
              handleSupplierChange(
                event.target.value,
              )
          }
        >
          <option value="">
            {loadingSuppliers
              ? "جاري تحميل الموردين..."
              : "اختر المورد"}
          </option>

          {suppliers.map(
            supplier => (
              <option
                key={
                  supplier.id
                }
                value={
                  supplier.id
                }
              >
                {supplier.nameAr ||
                  supplier.name}{" "}
                — {supplier.code}
              </option>
            ),
          )}
        </select>


        {selectedSupplier && (
          <div className="selected-supplier-info">
            <div>
              <span>
                المورد
              </span>

              <strong>
                {selectedSupplier.nameAr ||
                  selectedSupplier.name}
              </strong>
            </div>

            <div>
              <span>
                الكود
              </span>

              <strong>
                {selectedSupplier.code}
              </strong>
            </div>

            <div>
              <span>
                العملة الافتراضية
              </span>

              <strong>
                {selectedSupplier.currencyCode}
              </strong>
            </div>
          </div>
        )}
      </section>


      {selectedSupplierId && (
        <>
          <section className="supplier-opening-summary-grid">
            <article className="supplier-opening-summary-card">
              <span>
                مستحق للمورد
              </span>

              <strong>
                {formatAmount(
                  summary?.payableBaseAmount ??
                    0,
                )}
              </strong>

              <small>
                {summary?.baseCurrencyCode ||
                  "IQD"}
              </small>
            </article>


            <article className="supplier-opening-summary-card">
              <span>
                مستحق لنا
              </span>

              <strong>
                {formatAmount(
                  summary?.receivableBaseAmount ??
                    0,
                )}
              </strong>

              <small>
                {summary?.baseCurrencyCode ||
                  "IQD"}
              </small>
            </article>


            <article className="supplier-opening-summary-card main">
              <span>
                صافي المستحق
              </span>

              <strong>
                {formatAmount(
                  summary?.netPayableBaseAmount ??
                    0,
                )}
              </strong>

              <small>
                {summary?.baseCurrencyCode ||
                  "IQD"}
              </small>
            </article>
          </section>


          <section className="supplier-opening-card">
            <div className="supplier-opening-section-title">
              <div>
                <h2>
                  {editingBalanceId
                    ? "تعديل الرصيد الافتتاحي"
                    : "إضافة رصيد افتتاحي"}
                </h2>

                <p>
                  سجل الرصيد السابق
                  للمورد مع العملة والتاريخ
                  وسعر الصرف.
                </p>
              </div>

              {editingBalanceId && (
                <button
                  type="button"
                  className="supplier-opening-button secondary"
                  onClick={
                    resetForm
                  }
                >
                  إلغاء التعديل
                </button>
              )}
            </div>


            <form
              className="supplier-opening-form"
              onSubmit={
                handleSubmit
              }
            >
              <label>
                <span>
                  نوع الرصيد
                </span>

                <select
                  value={
                    form.balanceType
                  }
                  onChange={
                    event =>
                      setForm(
                        previous => ({
                          ...previous,

                          balanceType:
                            event.target
                              .value as SupplierOpeningBalanceType,
                        }),
                      )
                  }
                >
                  <option value="payable">
                    مستحق للمورد
                  </option>

                  <option value="receivable">
                    مستحق لنا
                  </option>
                </select>
              </label>


              <label>
                <span>
                  المبلغ
                </span>

                <input
                  type="number"
                  min="0"
                  step="any"
                  required
                  value={
                    form.amount
                  }
                  onChange={
                    event =>
                      setForm(
                        previous => ({
                          ...previous,

                          amount:
                            event.target
                              .value,
                        }),
                      )
                  }
                  placeholder="0"
                />
              </label>


              <label>
                <span>
                  العملة
                </span>

                <input
                  type="text"
                  maxLength={3}
                  required
                  value={
                    form.currencyCode
                  }
                  onChange={
                    event =>
                      setForm(
                        previous => ({
                          ...previous,

                          currencyCode:
                            event.target
                              .value
                              .toUpperCase(),
                        }),
                      )
                  }
                  placeholder="IQD"
                />
              </label>


              <label>
                <span>
                  العملة الأساسية
                </span>

                <input
                  type="text"
                  maxLength={3}
                  required
                  value={
                    form.baseCurrencyCode
                  }
                  onChange={
                    event =>
                      setForm(
                        previous => ({
                          ...previous,

                          baseCurrencyCode:
                            event.target
                              .value
                              .toUpperCase(),
                        }),
                      )
                  }
                  placeholder="IQD"
                />
              </label>


              <label>
                <span>
                  سعر الصرف
                </span>

                <input
                  type="number"
                  min="0"
                  step="any"
                  required
                  value={
                    form.exchangeRate
                  }
                  onChange={
                    event =>
                      setForm(
                        previous => ({
                          ...previous,

                          exchangeRate:
                            event.target
                              .value,
                        }),
                      )
                  }
                />
              </label>


              <label>
                <span>
                  تاريخ الرصيد
                </span>

                <input
                  type="date"
                  required
                  value={
                    form.balanceDate
                  }
                  onChange={
                    event =>
                      setForm(
                        previous => ({
                          ...previous,

                          balanceDate:
                            event.target
                              .value,
                        }),
                      )
                  }
                />
              </label>


              <label>
                <span>
                  رقم المرجع
                </span>

                <input
                  type="text"
                  value={
                    form.referenceNumber
                  }
                  onChange={
                    event =>
                      setForm(
                        previous => ({
                          ...previous,

                          referenceNumber:
                            event.target
                              .value,
                        }),
                      )
                  }
                  placeholder="اختياري"
                />
              </label>


              <label className="full-width">
                <span>
                  الملاحظات
                </span>

                <textarea
                  rows={4}
                  value={
                    form.notes
                  }
                  onChange={
                    event =>
                      setForm(
                        previous => ({
                          ...previous,

                          notes:
                            event.target
                              .value,
                        }),
                      )
                  }
                  placeholder="مثال: رصيد مرحل من النظام السابق"
                />
              </label>


              <div className="supplier-opening-form-actions">
                <button
                  type="submit"
                  className="supplier-opening-button primary"
                  disabled={
                    saving
                  }
                >
                  {saving
                    ? "جاري الحفظ..."
                    : editingBalanceId
                    ? "حفظ التعديل"
                    : "إضافة الرصيد"}
                </button>
              </div>
            </form>
          </section>


          <section className="supplier-opening-card">
            <div className="supplier-opening-section-title">
              <div>
                <h2>
                  سجل الأرصدة
                </h2>

                <p>
                  جميع الأرصدة الافتتاحية
                  المسجلة لهذا المورد.
                </p>
              </div>

              <button
                type="button"
                className="supplier-opening-button secondary"
                disabled={
                  loadingBalances
                }
                onClick={
                  () =>
                    void loadBalances(
                      selectedSupplierId,
                    )
                }
              >
                تحديث
              </button>
            </div>


            {loadingBalances ? (
              <div className="supplier-opening-empty">
                جاري تحميل الأرصدة...
              </div>
            ) : balances.length ===
              0 ? (
              <div className="supplier-opening-empty">
                لا توجد أرصدة افتتاحية
                لهذا المورد.
              </div>
            ) : (
              <div className="supplier-opening-table-wrap">
                <table className="supplier-opening-table">
                  <thead>
                    <tr>
                      <th>
                        النوع
                      </th>

                      <th>
                        المبلغ
                      </th>

                      <th>
                        العملة
                      </th>

                      <th>
                        سعر الصرف
                      </th>

                      <th>
                        القيمة الأساسية
                      </th>

                      <th>
                        التاريخ
                      </th>

                      <th>
                        المرجع
                      </th>

                      <th>
                        الحالة
                      </th>

                      <th>
                        الإجراءات
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {balances.map(
                      item => (
                        <tr
                          key={
                            item.balanceId
                          }
                        >
                          <td>
                            <span
                              className={`supplier-opening-type ${item.balanceType}`}
                            >
                              {item.balanceType ===
                              "payable"
                                ? "مستحق للمورد"
                                : "مستحق لنا"}
                            </span>
                          </td>

                          <td>
                            {formatAmount(
                              item.amount,
                            )}
                          </td>

                          <td>
                            {item.currencyCode}
                          </td>

                          <td>
                            {formatAmount(
                              item.exchangeRate,
                            )}
                          </td>

                          <td>
                            {formatAmount(
                              item.baseAmount,
                            )}{" "}
                            {
                              item.baseCurrencyCode
                            }
                          </td>

                          <td>
                            {item.balanceDate}
                          </td>

                          <td>
                            {item.referenceNumber ||
                              "—"}
                          </td>

                          <td>
                            <span
                              className={`supplier-opening-status ${item.status}`}
                            >
                              {item.status ===
                              "active"
                                ? "فعال"
                                : "ملغي"}
                            </span>
                          </td>

                          <td>
                            <div className="supplier-opening-actions">
                              {item.status ===
                                "active" && (
                                <>
                                  <button
                                    type="button"
                                    className="table-action edit"
                                    onClick={
                                      () =>
                                        handleEdit(
                                          item,
                                        )
                                    }
                                  >
                                    تعديل
                                  </button>

                                  <button
                                    type="button"
                                    className="table-action cancel"
                                    onClick={
                                      () =>
                                        void handleCancelBalance(
                                          item,
                                        )
                                    }
                                  >
                                    إلغاء
                                  </button>
                                </>
                              )}

                              {item.status ===
                                "cancelled" && (
                                <span className="cancel-reason">
                                  {item.cancellationReason ||
                                    "تم الإلغاء"}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}


export default SupplierOpeningBalances;